/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { useWorkspaceMetadata } from '../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { useProcesses } from '../../../../api/hooks/processes/useProcesses'
import { useSelector } from 'react-redux'
import { useToggle } from '../../../../hooks/useToggle'
import { ScoopTheme } from '../../../Insights/Style'
import { getWorkspaceTheme } from '../../../Insights/utils'
import { FilterGenerator } from './FilterGenerator'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useToast } from '../../../ui'

const diagramInitialState = {
    diagramName: null,
    supportThreshold: 20,
    weightThreshold: 20,
    dropdownFilter: [],
    filterSnapshotDate: null,
    selectedFiltersV2: [],
    maxWidth: window.innerWidth - 600,
    maxHeight: window.innerHeight - 100,
    startDate: dayjs().subtract(1, 'year').format('MM/DD/YYYY'),
    endDate: dayjs().format('MM/DD/YYYY'),
    theme: 'None',
    step: 'next',
    isStepBased: true,
    numberOfSteps: 7,
    sankeyPeriod: 'Weekly',
    nodeColor: '#1e6e53', // Default node color
    backgroundColor: 'transparent', // Default background color
    pathColor: '#01213d5a', // Default path color for Sankey
    sankeyOption: {
        animation: false,
        series: {
            type: 'sankey',
            nodeAlign: 'right',
            layout: 'none',
            emphasis: {
                focus: 'trajectory',
            },
        },
    },
}

export const useProcessesData = (embeddedDiagram, processDiagramToEdit) => {
    const { workspaceMetadata } = useWorkspaceMetadata()
    const {
        processes: preliminaryData,
        listProcessLoading: preliminaryDataLoading,
        isLoadingReportSeries,
        reportSeriesData,
        saveDiagramMutation,
        deleteDiagramMutation,
        useProcessDiagramPreferences,
        getSankeyMutation,
        getProcessInsightDiagramMutation,
    } = useProcesses()
    const toast = useToast()
    const isRenderedInACanvas = Boolean(embeddedDiagram)
    const pointerEvents = isRenderedInACanvas && !embeddedDiagram.clickable ? 'none' : 'all'
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const userID = useSelector((state) => state.auth.userID)
    const queryClient = useQueryClient()

    const [isEmptyData, setIsEmptyData] = useState(true)
    const [isError, setIsError] = useState(false)
    const [isLoadingHook, setIsLoadingHook] = useState(false)
    const [diagramState, setDiagramState] = useState(diagramInitialState)
    const [loadTempDiagramName, setloadTempDiagramName] = useState(null)
    const [loadedDiagram, setLoadedDiagram] = useState(false)

    const { value: isOpenLoadDiagramModal, onToggle: onToggleLoadDiagramModal } = useToggle()
    const { value: isOpenFiltersDiagramModal, onToggle: onToggleFiltersDiagramModal } = useToggle()
    const { value: isOpenSaveAsDiagramModal, onToggle: onToggleSaveAsDiagramModal } = useToggle()
    const { value: isOpenDeleteDiagramModal, onToggle: onToggleDeleteDiagramModal } = useToggle()

    const { fetchProcessDiagramPreferences } = useProcessDiagramPreferences(
        embeddedDiagram
            ? embeddedDiagram.processDiagram
            : processDiagramToEdit || loadTempDiagramName
    )

    //   ======================
    //  |  Save As Diagram    |
    //   ======================

    const handleSave = (diagramName) => {
        if (!diagramName) return
        setIsLoadingHook(true)
        saveDiagramMutation({
            data: {
                userID,
                diagramName,
                workspaceID,
                json: diagramState,
            },
            onSuccess: () => {
                const diagramTempState = _.cloneDeep(diagramState)
                diagramTempState.diagramName = diagramName
                setDiagramState(diagramTempState)
                queryClient.invalidateQueries({ queryKey: ['processDiagramPreferences'] })
                toast.success('Diagram saved successfully!')
                setIsLoadingHook(false)
            },
            onError: () => {
                toast.error('Error saving diagram')
                setIsLoadingHook(false)
            },
        })
    }

    //   ======================
    //  |  Delete Diagram     |
    //   ======================
    const handleDeleteDiagram = () => {
        if (!diagramState.diagramName) return

        deleteDiagramMutation({
            data: {
                userID,
                diagramName: diagramState.diagramName,
                workspaceID,
            },
            onSuccess: () => {
                toast.success('Diagram deleted successfully!')
                // Reset the diagram state
                setDiagramState(diagramInitialState)
                setIsEmptyData(true)
                // Invalidate the query to refresh the list of diagrams
                queryClient.invalidateQueries({ queryKey: ['processDiagramPreferences'] })
            },
            onError: () => {
                toast.error('Error deleting diagram')
            },
        })
    }

    //   ======================
    //  |  Create New Diagram  |
    //   ======================

    const [datasetToAnalyzeForm, setDatasetToAnalyzeForm] = useState({
        dataset: '',
        attribute: '',
        value: '',
    })

    const [datasetDropdownOptions, setDatasetDropDownOptions] = useState([])
    const [datasetDropdownLoading, setDatasetDropdownLoading] = useState(true)
    const [columnDropdownOptions, setColumnDropdownOptions] = useState([])
    const [valueDropdownOptions, setValueDropdownOptions] = useState([])
    const [themeDropdownOptions, setThemeDropDownOptions] = useState([])
    const [startingValuesOptions, setStartingValuesOptions] = useState([])

    // Invalidate process diagram preferences when workspace changes
    useEffect(() => {
        queryClient.invalidateQueries({ queryKey: ['processDiagramPreferences'] })
    }, [workspaceID])

    useEffect(() => {
        if (!preliminaryDataLoading && !isLoadingReportSeries) {
            if (preliminaryData && reportSeriesData) {
                generateDatasetDropdownOptions()
            } else {
                setDatasetDropdownLoading(true)
            }
        }
    }, [preliminaryDataLoading, isLoadingReportSeries, preliminaryData, reportSeriesData])

    // On load Diagram embedded or from edit
    useEffect(() => {
        if (embeddedDiagram) {
            handleLoadDiagram()
        } else if (processDiagramToEdit && !loadedDiagram) {
            handleLoadDiagram()
            setLoadedDiagram(true)
        }
    }, [JSON.stringify(embeddedDiagram), processDiagramToEdit])

    useEffect(() => {
        generateThemeOptions()
    }, [workspaceMetadata])

    useEffect(() => {
        const tempDiagramState = _.cloneDeep(diagramState)
        if (embeddedDiagram && embeddedDiagram.embedded != null) {
            tempDiagramState.maxWidth = embeddedDiagram.width
            tempDiagramState.maxHeight = embeddedDiagram.height

            const scaledNodes = fitNodesToWindow(
                tempDiagramState.nodeDataArray,
                embeddedDiagram.width,
                embeddedDiagram.height
            )
            const updatedNodeDataArray = tempDiagramState.nodeDataArray?.map((node, index) => {
                const newLoc = scaledNodes[index].loc
                return {
                    ...node,
                    loc: newLoc,
                }
            })
            tempDiagramState.nodeDataArray = updatedNodeDataArray

            const scaleOrigNodes = fitNodesToWindow(
                tempDiagramState.originalNodeDataArray,
                embeddedDiagram.width,
                embeddedDiagram.height
            )
            const updatedOriginalNodeDataArray = tempDiagramState.originalNodeDataArray?.map(
                (node, index) => {
                    const newLoc = scaleOrigNodes[index]
                    const loc = typeof node.loc === 'string' ? node.loc : `${newLoc.x} ${newLoc.y}`
                    return {
                        ...node,
                        loc: loc,
                    }
                }
            )
            tempDiagramState.originalNodeDataArray = updatedOriginalNodeDataArray
            setDiagramState(tempDiagramState)
        }
    }, [embeddedDiagram?.width, embeddedDiagram?.height])

    // On dataset seleted change
    useEffect(() => {
        if (preliminaryData && reportSeriesData && datasetToAnalyzeForm.dataset) {
            generateColumnDropdownOptions()
            // Only clear the dependent fields if it's not a loaded diagram or if the dataset actually changed
            if (!loadedDiagram) {
                onChangeDatasetToAnalyzeForm('', 'attribute')
                onChangeDatasetToAnalyzeForm('', 'value')
            }
        }
    }, [datasetToAnalyzeForm.dataset])

    // // On attribute selected change
    useEffect(() => {
        if (
            preliminaryData &&
            reportSeriesData &&
            datasetToAnalyzeForm.dataset &&
            datasetToAnalyzeForm.attribute
        ) {
            generateValueDropdownOptions()
            // Only clear the value field if it's not a loaded diagram
            if (!loadedDiagram) {
                onChangeDatasetToAnalyzeForm('', 'value')
            }
        }
    }, [datasetToAnalyzeForm.attribute])

    // Generate Starting values options when dataset or attribute changes
    useEffect(() => {
        if (diagramState.reportSeriesTableID && diagramState.attribute && preliminaryData) {
            generateOptionsDropdownSankeyStartingValues()
        }
    }, [diagramState.reportSeriesTableID, diagramState.attribute, preliminaryData])

    // Fn that generate the theme option
    const generateThemeOptions = () => {
        const themeOptions =
            workspaceMetadata?.themes?.map((theme) => ({
                label: `${theme.themeName} (${theme.canvasName})`,
                value: theme.themeID,
            })) || []
        setThemeDropDownOptions(themeOptions)
    }

    // Fn that generate the array for Dataset dropdown items.
    const generateDatasetDropdownOptions = () => {
        setDatasetDropdownLoading(true)

        // Use setTimeout to ensure the loading state is visible
        setTimeout(() => {
            if (preliminaryData && reportSeriesData) {
                const datasetOptions = preliminaryData
                    .map((pdata) => {
                        const found = reportSeriesData.find(
                            (report) => report.reportSeriesID === pdata.reportSeriesTableID
                        )
                        return found
                            ? {
                                  label: found.inboxLabel,
                                  value: pdata.reportSeriesTableID.toString(),
                              }
                            : null
                    })
                    .filter((option) => option !== null) // Filter out null values
                setDatasetDropDownOptions(datasetOptions)
            }
            setDatasetDropdownLoading(false)
        }, 300) // Small delay to show loading state
    }

    // Fn that generate the array for Column dropdown items.
    const generateColumnDropdownOptions = (datasetLoaded) => {
        if ((datasetToAnalyzeForm.dataset || datasetLoaded) && preliminaryData) {
            const report = preliminaryData.find(
                (pdata) =>
                    pdata.reportSeriesTableID.toString() === datasetToAnalyzeForm.dataset ||
                    pdata.reportSeriesTableID.toString() === datasetLoaded
            )
            if (report) {
                const columnOptions = report.changeColumns.map((c) => ({
                    label: c.columnName,
                    value: c.columnName,
                }))
                setColumnDropdownOptions(columnOptions)
            }
        }
    }

    // Fn that generate the array for Value dropdown items.
    const generateValueDropdownOptions = (datasetLoaded, attrLoaded) => {
        if (
            ((datasetToAnalyzeForm.attribute && datasetToAnalyzeForm.dataset) ||
                (datasetLoaded && attrLoaded)) &&
            preliminaryData
        ) {
            const report = preliminaryData.find(
                (pData) =>
                    pData.reportSeriesTableID.toString() === datasetToAnalyzeForm.dataset ||
                    pData.reportSeriesTableID.toString() === datasetLoaded
            )
            const column = report?.changeColumns.find(
                (c) =>
                    c.columnName === datasetToAnalyzeForm.attribute || c.columnName === attrLoaded
            )
            if (column) {
                const successOptions = column.values.map((v) => ({
                    label: `${v}`,
                    value: `${v}`,
                }))
                setValueDropdownOptions(successOptions)
            }
        }
    }

    //On change inputs
    const onChangeDatasetToAnalyzeForm = (value, field) => {
        if (field === 'dataset')
            setDiagramState((prev) => ({ ...prev, reportSeriesTableID: value }))
        if (field === 'attribute') setDiagramState((prev) => ({ ...prev, attribute: value }))
        if (field === 'value') setDiagramState((prev) => ({ ...prev, successValue: value }))
        setDatasetToAnalyzeForm((prev) => ({ ...prev, [field]: value }))
    }

    // ---------END NEW DIAGRAMS------------------

    // Fn that generate the array for Sankey Starting Values dropdown items.
    const generateOptionsDropdownSankeyStartingValues = (
        reportSeriesTableID = null,
        attribute = null
    ) => {
        const columnList = []
        const tableId = reportSeriesTableID || diagramState.reportSeriesTableID
        const columnName = attribute || diagramState.attribute

        if (preliminaryData && tableId && columnName) {
            preliminaryData.forEach((report) => {
                if (report.reportSeriesTableID === tableId) {
                    report.changeColumns.forEach((column) => {
                        if (column.columnName === columnName) {
                            columnList.push(...column.values)
                        }
                    })
                }
            })
        }

        setStartingValuesOptions(columnList)
        return columnList
    }

    // Fn for displayLinksAboveThreshold and onToggleShowNodes
    const doesLinkExist = (l, linkDataArray) => {
        const isFound = linkDataArray.filter((d) => d.from === l.from && d.to === l.to)
        return isFound.length > 0
    }
    // call to only display links that are above a threshold, both for liveValue and lieWeight, respectively
    const displayLinksAboveThreshold = ({ weightThreshold, supportThreshold, linksData }) => {
        if (!linksData) return

        const t = weightThreshold / 100
        const sp = supportThreshold / 100
        const filterLinkData = _.cloneDeep(linksData)
        linksData.forEach((line) => {
            if (line.lineWeight < t || line.supportPercentile < sp) {
                const index = filterLinkData?.findIndex((x) => _.isEqual(x, line))
                if (index !== -1) {
                    filterLinkData.splice(index, 1)
                }
            }
        })

        linksData.forEach((l) => {
            if (l.lineWeight >= t && l.supportPercentile >= sp) {
                if (!doesLinkExist(l, filterLinkData)) {
                    filterLinkData.push(l)
                }
            }
        })

        setDiagramState((prev) => ({
            ...prev,
            linkDataArray: [...filterLinkData],
            weightThreshold,
            supportThreshold,
        }))
    }

    // the users selected nodes to show/hide
    const onToggleShowNodes = (node) => {
        const nodes = []
        const originalNodes = _.cloneDeep(diagramState.originalNodeDataArray)
        const filterLinkData = _.cloneDeep(diagramState.linkDataArray)
        const toDeleteIndex = originalNodes.findIndex((x) => x.node === node.node)

        if (toDeleteIndex !== -1) {
            originalNodes[toDeleteIndex].isChecked = !originalNodes[toDeleteIndex].isChecked
        }

        var nodeKeysToKeep = originalNodes.filter((x) => x.isChecked === true).map((x) => x.key)

        var nodeKeysToDelete = originalNodes.filter((x) => x.isChecked === false).map((x) => x.key)

        originalNodes.forEach((n) => {
            if (!nodeKeysToKeep.includes(n.key)) {
                const index = nodes.findIndex((x) => _.isEqual(x, n))
                if (index !== -1) {
                    nodes.splice(index, 1)
                }
            }
        })

        // //add nodes that are in the list of nodes to keep
        originalNodes.forEach((n) => {
            if (nodeKeysToKeep.includes(n.key)) {
                nodes.push(n)
            }
        })

        // //remove all links that don't touch the nodes to keep (note: no idea why need to run this a bunch of times for all to take effect, but need to)
        diagramState.linkDataArray.forEach((l) => {
            if (nodeKeysToDelete.includes(l.from) || nodeKeysToDelete.includes(l.to)) {
                const index = filterLinkData.findIndex((x) => _.isEqual(x, l))
                if (index !== -1) {
                    filterLinkData.splice(index, 1)
                }
            }
        })

        diagramState.originalLinkDataArray.forEach((l) => {
            if (!nodeKeysToDelete.includes(l.from) && !nodeKeysToDelete.includes(l.to)) {
                if (!doesLinkExist(l, filterLinkData)) {
                    filterLinkData.push(l)
                }
            }
        })

        displayLinksAboveThreshold({
            linksData: filterLinkData,
            supportThreshold: diagramState.supportThreshold,
            weightThreshold: diagramState.weightThreshold,
        })
        setDiagramState((prev) => ({
            ...prev,
            nodeDataArray: [...nodes],
            originalNodeDataArray: [...originalNodes],
        }))
    }

    const handleDiagramEvent = (event) => {
        const selectedNode = event.subject.first()

        if (!selectedNode) return

        // Update node location with go.Point
        let updatedLocation = selectedNode.location.copy()
        updatedLocation = `${updatedLocation.x} ${updatedLocation.y}`

        const data = _.cloneDeep(diagramState.nodeDataArray)
        const originalData = _.cloneDeep(diagramState.originalNodeDataArray)

        // Grab Indexes
        const existingNodeIndex = data?.findIndex((x) => x.key === selectedNode.key)
        const originalExistingNodeIndex = originalData?.findIndex((x) => x.key === selectedNode.key)

        // Update Node location
        if (existingNodeIndex !== -1) {
            data[existingNodeIndex].loc = updatedLocation
            setDiagramState((prev) => ({ ...prev, nodeDataArray: [...data] }))
        }

        if (originalExistingNodeIndex !== -1) {
            originalData[originalExistingNodeIndex].loc = updatedLocation
            setDiagramState((prev) => ({ ...prev, originalNodeDataArray: [...originalData] }))
        }
    }

    const getColor = () => {
        if (diagramState?.theme?.colorScheme?.darkTheme) {
            return '#fff'
        } else return '#000'
    }

    const getSankeyTheme = () => {
        if (diagramState?.theme?.colorScheme) {
            const temp = { ...ScoopTheme }
            temp.color = diagramState.theme.colorScheme.colors.map((c) => c.val)
            temp.backgroundColor = diagramState.theme.colorScheme.backgroundColor
            return temp
        } else {
            return ScoopTheme
        }
    }

    const getOptionWithStyles = () => {
        if (diagramState?.sankeyOption?.series?.links) {
            const temp = {
                ...diagramState.sankeyOption,
                backgroundColor: diagramState.backgroundColor,
                label: { color: getColor() },
            }
            temp.series.links.forEach((link, i) => {
                temp.series.links[i] = {
                    ...link,
                    lineStyle: { color: diagramState.pathColor || '#01213d5a', opacity: 1 },
                }
            })
            return temp
        }
        return {
            animation: false,
            backgroundColor: diagramState.backgroundColor,
            series: {
                type: 'sankey',
                nodeAlign: 'right',
                layout: 'none',
                emphasis: {
                    focus: 'trajectory',
                },
                label: { color: getColor() },
            },
        }
    }

    const handleRequestDiagram = (isNew = false, extraPayload = {}) => {
        // This function only handles Process Diagrams
        setIsLoadingHook(true)
        setIsError(false)

        const onSuccessCallback = (res) => {
            // Check if response has valid data
            const hasValidData = res?.nodeDataArray && res?.nodeDataArray.length > 0

            if (!hasValidData) {
                // Preserve panel configuration but clear diagram data
                setDiagramState((prevState) => ({
                    ...prevState,
                    nodeDataArray: [],
                    originalNodeDataArray: [],
                    linkDataArray: [],
                    originalLinkDataArray: [],
                    sankeyOption: diagramInitialState.sankeyOption, // Reset sankey option
                }))
                setIsEmptyData(false)
                setIsError(true)
                setIsLoadingHook(false)
                toast.error(
                    'Unable to render diagram. No valid data found for the selected configuration.'
                )
                return
            }

            if (res.nodeDataArray) {
                const updatedNodeDataArray = res.nodeDataArray?.map((n, counter) => {
                    return {
                        key: n.key,
                        node: n.node,
                        value: n.value,
                        isChecked: true,
                        order: counter,
                        selectedSuccess: isNew
                            ? datasetToAnalyzeForm.value
                            : diagramState.successValue,
                        duration: n.duration || null,
                        loc: `${n.x} ${n.y}`,
                    }
                })
                // Scale the nodes to fit the window
                const scaledNodes = fitNodesToWindow(
                    updatedNodeDataArray,
                    diagramState.maxWidth,
                    diagramState.maxHeight
                )

                // Update the locations of the nodes
                const scaledNodeDataArray = updatedNodeDataArray?.map((node, index) => {
                    return {
                        ...node,
                        loc: scaledNodes[index].loc,
                    }
                })

                setDiagramState((prevState) => {
                    const tempDiagram = _.cloneDeep(prevState)
                    tempDiagram.nodeDataArray = scaledNodeDataArray
                    tempDiagram.originalNodeDataArray = scaledNodeDataArray
                    tempDiagram.linkDataArray = res.linkDataArray
                    tempDiagram.originalLinkDataArray = res.linkDataArray
                    // Only set 'New Diagram' if there's no existing name
                    if (!tempDiagram.diagramName) {
                        tempDiagram.diagramName = 'New Diagram'
                    }
                    tempDiagram.isSankey = false // Explicitly set to false for Process Diagrams
                    // Preserve the step value from the request
                    if (extraPayload.step) {
                        tempDiagram.step = extraPayload.step
                    }
                    return tempDiagram
                })
                displayLinksAboveThreshold({
                    weightThreshold: diagramState.weightThreshold,
                    supportThreshold: diagramState.supportThreshold,
                    linksData: [...res.linkDataArray],
                })
                setIsEmptyData(false)
                setIsLoadingHook(false)
            }
        }

        const onErrorCallback = (error) => {
            // Preserve panel configuration but clear diagram data
            setDiagramState((prevState) => ({
                ...prevState,
                nodeDataArray: [],
                originalNodeDataArray: [],
                linkDataArray: [],
                originalLinkDataArray: [],
                sankeyOption: diagramInitialState.sankeyOption, // Reset sankey option
            }))
            setIsEmptyData(false)
            setIsError(true)
            setIsLoadingHook(false)
            toast.error(
                'Process diagram request failed. Please check your configuration and try again.'
            )
        }

        const payload = {
            reportSeriesTableID: isNew
                ? datasetToAnalyzeForm.dataset
                : diagramState.reportSeriesTableID || datasetToAnalyzeForm.dataset,
            attribute: isNew
                ? datasetToAnalyzeForm.attribute
                : diagramState.attribute || datasetToAnalyzeForm.attribute,
            successValue: isNew
                ? datasetToAnalyzeForm.value
                : diagramState.successValue || datasetToAnalyzeForm.value,
            filter: null,
            step: diagramState.step,
            startDate: diagramState.startDate,
            endDate: diagramState.endDate,
            isSankey: false,
        }

        // Use extraPayload.filter if it's explicitly provided (including null)
        // Otherwise generate from current state
        payload.filter =
            'filter' in extraPayload
                ? extraPayload.filter
                : FilterGenerator(
                      diagramState.selectedFiltersV2,
                      diagramState.filterSnapshotDate,
                      diagramState.reportSeriesTableID,
                      reportSeriesData
                  )

        getProcessInsightDiagramMutation({
            payload: payload,
            onSuccess: onSuccessCallback,
            onError: onErrorCallback,
        })
    }

    const handleFetchSankey = ({
        isStepBased,
        numberOfSteps,
        sankeyPeriod,
        allowedStartingValues,
        result,
        diagramName = null,
    }) => {
        //  Sankey props request.
        setIsLoadingHook(true)
        setIsError(false)
        const onSuccessCallback = (data) => {
            setDiagramState((prevState) => {
                const tempDiagramState = _.cloneDeep(prevState)
                tempDiagramState.isSankey = true
                const newSankeyOption = { ...prevState.sankeyOption }
                newSankeyOption.series = { ...newSankeyOption.series }
                newSankeyOption.series.links = data.links
                newSankeyOption.series.data = data.data
                tempDiagramState.sankeyOption = newSankeyOption
                // Use the passed diagramName if provided, otherwise keep existing or set default
                if (diagramName) {
                    tempDiagramState.diagramName = diagramName
                } else if (!tempDiagramState.diagramName) {
                    tempDiagramState.diagramName = 'New Sankey Diagram'
                }
                tempDiagramState.isStepBased =
                    isStepBased !== undefined ? isStepBased : tempDiagramState.isStepBased
                tempDiagramState.numberOfSteps = numberOfSteps || tempDiagramState.numberOfSteps
                tempDiagramState.sankeyPeriod = sankeyPeriod || tempDiagramState.sankeyPeriod
                tempDiagramState.allowedStartingValues =
                    allowedStartingValues !== undefined
                        ? allowedStartingValues
                        : tempDiagramState.allowedStartingValues

                // Preserve the filters from the result if they were passed
                if (result && result.selectedFiltersV2) {
                    tempDiagramState.selectedFiltersV2 = result.selectedFiltersV2
                }

                return tempDiagramState
            })
            toast.success('Sankey diagram loaded successfully')
            setIsEmptyData(false)
            setIsLoadingHook(false)
            setIsError(false)
        }
        const onErrorCallback = () => {
            // Preserve panel configuration but clear diagram data
            setDiagramState((prevState) => ({
                ...prevState,
                sankeyOption: diagramInitialState.sankeyOption, // Reset sankey option
                nodeDataArray: [],
                originalNodeDataArray: [],
                linkDataArray: [],
                originalLinkDataArray: [],
            }))
            toast.error(
                'Sankey diagram request failed. Please check your configuration and try again.'
            )
            setIsLoadingHook(false)
            setIsEmptyData(false)
            setIsError(true)
        }

        let payload = {}
        if (result) {
            // This is when is triggered from load diagram
            payload = {
                reportSeriesTableID: result.selectedReportSeriesTable,
                attribute: result.selectedColumn,
                successValue: result.selectedSuccess,
                filter: FilterGenerator(
                    result.selectedFiltersV2,
                    result.filterSnapshotDate,
                    result.selectedReportSeriesTable,
                    reportSeriesData
                ),
                startDate: result.startDate,
                endDate: result.endDate,
                step: result.step,
                stepBased: result.stepBased,
                sankeyPeriod: result.sankeyPeriod,
                numSteps: result.numSteps,
                startingValues: result.startingValues,
            }
            if (payload?.startingValues?.length === 0) {
                delete payload.startingValues
            }
        } else {
            // This is when is triggered from a filter
            payload = {
                reportSeriesTableID:
                    diagramState.reportSeriesTableID || datasetToAnalyzeForm.dataset,
                attribute: diagramState.attribute || datasetToAnalyzeForm.attribute,
                successValue: diagramState.successValue || datasetToAnalyzeForm.value,
                filter: FilterGenerator(
                    diagramState.selectedFiltersV2,
                    diagramState.filterSnapshotDate,
                    diagramState.selectedReportSeriesTable,
                    diagramState.reportSeriesData
                ),
                startDate: diagramState.startDate,
                endDate: diagramState.endDate,
                step: diagramState.step,
                stepBased: isStepBased,
                sankeyPeriod: sankeyPeriod || diagramState.sankeyPeriod,
                numSteps: numberOfSteps || diagramState.numberOfSteps,
                startingValues: allowedStartingValues || diagramState.allowedStartingValues,
            }
            if (isStepBased) {
                delete payload.sankeyPeriod
            }
            if (payload?.startingValues?.length === 0) {
                delete payload.startingValues
            }
        }
        getSankeyMutation({ payload, onSuccess: onSuccessCallback, onError: onErrorCallback })
    }

    // arrange nodes within a given window -- called from multiple places
    const fitNodesToWindow = (nodes, width, height) => {
        const nodeSize = 140 // Assuming nodes are 140x140 units in size
        let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity

        // Find the current bounds
        nodes?.forEach((node) => {
            let x, y
            if (typeof node.loc === 'string') {
                ;[x, y] = node.loc.split(' ').map(Number)
            } else {
                ;({ x, y } = node.loc)
            }
            minX = Math.min(minX, x)
            minY = Math.min(minY, y)
            maxX = Math.max(maxX, x + nodeSize) // Add node size to x to consider its full width
            maxY = Math.max(maxY, y + nodeSize) // Add node size to y to consider its full height
        })

        // Calculate scale factors and center offset
        const scaleX = (0.8 * width) / (maxX - minX)
        const scaleY = (0.8 * height) / (maxY - minY)
        const offsetX = (maxX + minX) / 2
        const offsetY = (maxY + minY) / 2

        // Normalize, scale, and offset node positions
        return nodes?.map((node) => {
            let x, y
            if (typeof node.loc === 'string') {
                ;[x, y] = node.loc.split(' ').map(Number)
            } else {
                ;({ x, y } = node.loc)
            }
            return {
                ...node,
                loc: `${(x - offsetX) * scaleX} ${(y - offsetY) * scaleY}`,
            }
        })
    }

    const handleLoadDiagram = () => {
        const onSuccessLoadDiagram = (res) => {
            const scaledNodes = fitNodesToWindow(
                res.body.json.nodeDataArray,
                embeddedDiagram ? embeddedDiagram.width : diagramState.maxWidth,
                embeddedDiagram ? embeddedDiagram.height : diagramState.maxHeight
            )
            const nodeDataArray = res.body.json.nodeDataArray?.map((node, index) => {
                const newLoc = scaledNodes[index].loc
                return {
                    ...node,
                    loc: newLoc,
                }
            })

            const scaleOrigNodes = fitNodesToWindow(
                res.body.json.originalNodeDataArray,
                embeddedDiagram ? embeddedDiagram.width : diagramState.maxWidth,
                embeddedDiagram ? embeddedDiagram.height : diagramState.maxHeight
            )
            const originalNodeDataArray = res.body.json.originalNodeDataArray?.map(
                (node, index) => {
                    const newLoc = scaleOrigNodes[index].loc
                    return {
                        ...node,
                        loc: newLoc,
                    }
                }
            )

            generateDatasetDropdownOptions()
            const tempClone = _.cloneDeep(diagramState)
            const data = res.body.json
            tempClone.diagramName = loadTempDiagramName
            if (data.themeID) {
                const theme = workspaceMetadata?.themes?.find((x) => x.themeID === data.themeID)
                tempClone.key = [theme, data.nodeColor]
                tempClone.theme = theme
            }
            // COLORS
            tempClone.nodeColor = data.nodeColor
            tempClone.pathColor = data.linkColor || '#01213d5a' // Default path color for Sankey
            tempClone.backgroundColor = data.backgroundColor

            // NODES
            tempClone.nodeDataArray = nodeDataArray
            tempClone.originalNodeDataArray = originalNodeDataArray
            tempClone.linkDataArray = data.linkDataArray
            tempClone.originalLinkDataArray = data.originalLinkDataArray
            tempClone.weightThreshold = data.weightThreshold
            tempClone.supportThreshold = data.supportThreshold

            tempClone.step = data.step

            tempClone.startDate = data.startDate
            tempClone.endDate = data.endDate

            tempClone.isStepBased = data.stepBased
            tempClone.numberOfSteps = data.numSteps
            tempClone.sankeyPeriod = data.sankeyPeriod

            // Set allowedStartingValues from loaded data
            tempClone.allowedStartingValues =
                data.startingValues || data.allowedStartingValues || []

            tempClone.isSankey = data.sankey
            const tableId = data.reportSeriesTableID || data.selectedReportSeriesTable
            const column = data.attribute || data.selectedColumn
            const successValue = data.successValue || data.selectedSuccess

            tempClone.reportSeriesTableID = tableId
            tempClone.attribute = column
            tempClone.successValue = successValue

            if (tableId && column && successValue) {
                setLoadedDiagram(true)
                generateColumnDropdownOptions(tableId)
                generateValueDropdownOptions(tableId, column)
                setDatasetToAnalyzeForm({
                    dataset: tableId,
                    attribute: column,
                    value: successValue,
                })
                // Reset loadedDiagram flag after a short delay to allow normal select behavior
                setTimeout(() => {
                    setLoadedDiagram(false)
                }, 500)
            }

            setDiagramState(tempClone)

            // Generate Starting values options after loading the diagram
            if (tableId && column) {
                generateOptionsDropdownSankeyStartingValues(tableId, column)
            }

            if (data.sankey || data.isSankey) {
                // Fetch Sankey with loaded starting values
                return handleFetchSankey({
                    isStepBased: data.stepBased || data.isStepBased,
                    numberOfSteps: data.numSteps || data.numberOfSteps,
                    sankeyPeriod: data.sankeyPeriod,
                    allowedStartingValues: data.startingValues || data.allowedStartingValues || [],
                    diagramName: loadTempDiagramName,
                    result: {
                        selectedReportSeriesTable: tableId,
                        selectedColumn: column,
                        selectedSuccess: successValue,

                        filter: null,
                        startDate: data.startDate,
                        endDate: data.endDate,
                        step: data.step,
                        stepBased: data.stepBased || data.isStepBased,
                        sankeyPeriod: data.sankeyPeriod,
                        numSteps: data.numSteps || data.numberOfSteps,
                        startingValues: data.startingValues || data.allowedStartingValues || [],
                    },
                })
            } else {
                // End of flow if not a sankey
                setIsEmptyData(false)
                setIsLoadingHook(false)
                setIsError(false)
            }
        }

        const onErrorLoadDiagram = () => {
            setIsLoadingHook(false)
            toast.error('Something went wrong, please try again later')
            setIsError(true)
        }
        setIsLoadingHook(true)
        fetchProcessDiagramPreferences(onSuccessLoadDiagram, onErrorLoadDiagram)
    }

    //  BASIC SETTERS
    const diagramSetNodeData = (value) => {
        setDiagramState((prev) => ({ ...prev, nodeDataArray: value }))
    }

    const diagramSetBackgroundColor = (value) => {
        setDiagramState((prev) => ({ ...prev, backgroundColor: value }))
    }

    const diagramSetNodeColor = (value) => {
        setDiagramState((prev) => ({
            ...prev,
            nodeColor: value,
        }))
    }

    const diagramSetIsSankey = (value) => {
        // Update state first

        setDiagramState((prev) => ({ ...prev, isSankey: value }))

        // Check if we should trigger diagram creation
        if (diagramState.diagramName) {
            // Existing diagram - switch between types
            if (value) {
                // Switching to Sankey
                setIsLoadingHook(true)
                handleFetchSankey({
                    isStepBased: diagramState.isStepBased,
                    numberOfSteps: diagramState.numberOfSteps,
                    sankeyPeriod: diagramState.sankeyPeriod,
                    allowedStartingValues: diagramState.allowedStartingValues,
                })
            } else {
                // Switching to Process Diagram
                handleRequestDiagram(false)
            }
        } else if (
            value &&
            datasetToAnalyzeForm.dataset &&
            datasetToAnalyzeForm.attribute &&
            datasetToAnalyzeForm.value
        ) {
            // No existing diagram but all fields are selected and switching to Sankey
            // First create the diagram data, then fetch Sankey
            setDiagramState((prev) => ({
                ...prev,
                reportSeriesTableID: datasetToAnalyzeForm.dataset,
                attribute: datasetToAnalyzeForm.attribute,
                successValue: datasetToAnalyzeForm.value,
            }))

            setIsLoadingHook(true)
            handleFetchSankey({
                isStepBased: diagramState.isStepBased,
                numberOfSteps: diagramState.numberOfSteps,
                sankeyPeriod: diagramState.sankeyPeriod,
                allowedStartingValues: diagramState.allowedStartingValues,
            })
        }
    }

    const diagramSetSankeyNumberOfSteps = (value) => {
        setDiagramState((prev) => ({ ...prev, numberOfSteps: value }))
    }

    const diagramSetSankeyPeriod = (value) => {
        setDiagramState((prev) => ({ ...prev, sankeyPeriod: value }))
    }

    const diagramSetSankeyIsStepBased = (value) => {
        setDiagramState((prev) => ({ ...prev, isStepBased: value }))
    }

    const diagramSetAllowedStartingValues = (value) => {
        setDiagramState((prev) => ({ ...prev, allowedStartingValues: value }))
        if (diagramState.diagramName && diagramState.isSankey) {
            handleFetchSankey({
                isStepBased: diagramState.isStepBased,
                numberOfSteps: diagramState.numberOfSteps,
                sankeyPeriod: diagramState.sankeyPeriod,
                allowedStartingValues: value,
            })
        }
    }

    const diagramSetPathColor = (value) => {
        setDiagramState((prev) => ({ ...prev, pathColor: value, sankeyKey: '' + Math.random() }))
    }

    const diagramSetFilterV2 = (value) => {
        setDiagramState((prev) => {
            const newState = { ...prev, selectedFiltersV2: value }

            // Trigger diagram refresh when filters are applied and a diagram is loaded
            if (newState.diagramName) {
                // Use setTimeout to ensure state update happens first
                setTimeout(() => {
                    if (newState.isSankey) {
                        handleFetchSankey({
                            isStepBased: newState.isStepBased,
                            numberOfSteps: newState.numberOfSteps,
                            sankeyPeriod: newState.sankeyPeriod,
                            allowedStartingValues: newState.allowedStartingValues,
                            result: {
                                selectedReportSeriesTable: newState.reportSeriesTableID,
                                selectedColumn: newState.attribute,
                                selectedSuccess: newState.successValue,
                                selectedFiltersV2: value,
                                filterSnapshotDate: newState.filterSnapshotDate,
                                startDate: newState.startDate,
                                endDate: newState.endDate,
                                step: newState.step,
                                stepBased: newState.isStepBased,
                                sankeyPeriod: newState.sankeyPeriod,
                                numSteps: newState.numberOfSteps,
                                startingValues: newState.allowedStartingValues,
                            },
                        })
                    } else {
                        const filter = FilterGenerator(
                            value,
                            newState.filterSnapshotDate,
                            newState.reportSeriesTableID,
                            reportSeriesData
                        )
                        handleRequestDiagram(false, { filter })
                    }
                }, 0)
            }

            return newState
        })
    }

    const diagramSetFilterSnapshotDate = (value) => {
        setDiagramState((prev) => {
            const newState = { ...prev, filterSnapshotDate: value }

            // Trigger diagram refresh when snapshot date is changed and a diagram is loaded
            if (newState.diagramName) {
                // Use setTimeout to ensure state update happens first
                setTimeout(() => {
                    if (newState.isSankey) {
                        handleFetchSankey({
                            isStepBased: newState.isStepBased,
                            numberOfSteps: newState.numberOfSteps,
                            sankeyPeriod: newState.sankeyPeriod,
                            allowedStartingValues: newState.allowedStartingValues,
                            result: {
                                selectedReportSeriesTable: newState.reportSeriesTableID,
                                selectedColumn: newState.attribute,
                                selectedSuccess: newState.successValue,
                                selectedFiltersV2: newState.selectedFiltersV2,
                                filterSnapshotDate: value,
                                startDate: newState.startDate,
                                endDate: newState.endDate,
                                step: newState.step,
                                stepBased: newState.isStepBased,
                                sankeyPeriod: newState.sankeyPeriod,
                                numSteps: newState.numberOfSteps,
                                startingValues: newState.allowedStartingValues,
                            },
                        })
                    } else {
                        const filter = FilterGenerator(
                            newState.selectedFiltersV2,
                            value,
                            newState.reportSeriesTableID,
                            reportSeriesData
                        )
                        handleRequestDiagram(false, { filter })
                    }
                }, 0)
            }

            return newState
        })
    }

    // New function to apply both filters and snapshot date together
    const diagramApplyFiltersAndSnapshotDate = (filters, snapshotDate) => {
        setDiagramState((prev) => {
            const newState = {
                ...prev,
                selectedFiltersV2: filters,
                filterSnapshotDate: snapshotDate,
            }

            // Trigger diagram refresh when filters are applied and a diagram is loaded
            if (newState.diagramName) {
                // Use setTimeout to ensure state update happens after setState completes
                setTimeout(() => {
                    if (newState.isSankey) {
                        handleFetchSankey({
                            isStepBased: newState.isStepBased,
                            numberOfSteps: newState.numberOfSteps,
                            sankeyPeriod: newState.sankeyPeriod,
                            allowedStartingValues: newState.allowedStartingValues,
                            result: {
                                selectedReportSeriesTable: newState.reportSeriesTableID,
                                selectedColumn: newState.attribute,
                                selectedSuccess: newState.successValue,
                                selectedFiltersV2: filters,
                                filterSnapshotDate: snapshotDate,
                                startDate: newState.startDate,
                                endDate: newState.endDate,
                                step: newState.step,
                                stepBased: newState.isStepBased,
                                sankeyPeriod: newState.sankeyPeriod,
                                numSteps: newState.numberOfSteps,
                                startingValues: newState.allowedStartingValues,
                            },
                        })
                    } else {
                        const filter = FilterGenerator(
                            filters,
                            snapshotDate,
                            newState.reportSeriesTableID,
                            reportSeriesData
                        )

                        handleRequestDiagram(false, { filter })
                    }
                }, 0)
            }

            return newState
        })
    }

    const diagramSetStep = (value) => {
        // Store the previous step value for potential rollback
        const prevValueOfStep = diagramState.step

        // Only proceed if we have a diagram name and the value is actually changing
        if (!diagramState.diagramName || value === prevValueOfStep) {
            return
        }

        // Optimistically update the step value
        setDiagramState((prev) => ({ ...prev, step: value }))

        // Make the API request with proper error handling
        handleRequestDiagram(false, { step: value })
    }

    const diagramSetTheme = (value) => {
        if (value === 'None') {
            setDiagramState((prev) => ({
                ...prev,
                theme: 'None',
                backgroundColor: 'transparent',
            }))
            return
        }
        const theme = getWorkspaceTheme(value, workspaceMetadata)

        setDiagramState((prev) => ({
            ...prev,
            theme: theme,
            backgroundColor: theme?.colorScheme?.backgroundColor || 'transparent',
        }))
    }

    const diagramSetStartDate = (value) => {
        setDiagramState((prev) => ({ ...prev, startDate: value }))
    }

    const diagramSetEndDate = (value) => {
        setDiagramState((prev) => ({ ...prev, endDate: value }))
    }

    return {
        isLoadingHook,
        isEmptyDataset: !preliminaryData || preliminaryData.length === 0,
        preliminaryDataLoading,
        isEmptyData,
        isError,
        isRenderedInACanvas,

        diagramState: { ...diagramState, handleDiagramEvent, setNodeDataArray: diagramSetNodeData },
        preliminaryData,

        //Create New diagram dropdowns
        datasetToAnalyzeForm,
        onChangeDatasetToAnalyzeForm,
        datasetDropdownOptions,
        datasetDropdownLoading,
        columnDropdownOptions,
        valueDropdownOptions,

        onToggleShowNodes,
        handleLoadDiagram,
        handleRequestDiagram,
        handleSave,

        displayLinksAboveThreshold,
        getSankeyTheme,
        handleFetchSankey,

        //Filters
        diagramSetFilterV2,
        diagramSetFilterSnapshotDate,
        diagramApplyFiltersAndSnapshotDate,

        //Sankey
        getOptionWithStyles,
        generateOptionsDropdownSankeyStartingValues,
        startingValuesOptions,
        diagramSetPathColor,

        // BASIC SETTERS
        diagramSetBackgroundColor,
        diagramSetNodeColor,
        diagramSetIsSankey,
        diagramSetSankeyNumberOfSteps,
        diagramSetSankeyPeriod,
        diagramSetSankeyIsStepBased,
        diagramSetAllowedStartingValues,
        diagramSetStep,

        //Modals
        isOpenLoadDiagramModal,
        onToggleLoadDiagramModal,
        isOpenFiltersDiagramModal,
        onToggleFiltersDiagramModal,
        isOpenSaveAsDiagramModal,
        onToggleSaveAsDiagramModal,
        isOpenDeleteDiagramModal,
        onToggleDeleteDiagramModal,
        handleDeleteDiagram,

        //Theme Select
        themeDropdownOptions,
        diagramSetTheme,
        pointerEvents,

        //TempStateForLoadDiagram
        loadTempDiagramName,
        setloadTempDiagramName,

        //Date setters
        diagramSetStartDate,
        diagramSetEndDate,
    }
}
